(() => {
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = (t) =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // quantizza a 0.5px per ridurre “shimmer” durante scroll
  const q05 = (v) => Math.round(v * 2) / 2;

  class PGMManager {
    static instances = new Set();
    static started = false;
    static rafId = 0;
    static lastW = 0;

    static start() {
      if (this.started) return;
      this.started = true;

      const tick = (now) => {
        const y =
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;

        this.instances.forEach((i) => i._tick(now, y));
        this.rafId = requestAnimationFrame(tick);
      };

      const onResize = () => {
        const w = window.innerWidth || 0;
        if (w === this.lastW) return;
        this.lastW = w;
        this.instances.forEach((i) => i.refresh(true));
      };

      window.addEventListener("resize", onResize);

      this.lastW = window.innerWidth || 0;
      this.instances.forEach((i) => i.refresh(true));
      this.rafId = requestAnimationFrame(tick);
    }
  }

  class ParallaxGalleryMarquee extends HTMLElement {
    connectedCallback() {
      this._scrollWrap = this.querySelector("[data-pgm-scroll]");
      this._stage = this.querySelector("[data-pgm-stage]");
      this._layers = Array.from(this.querySelectorAll("[data-pgm-layer]"));

      // marquee
      this._ticker = this.querySelector(".pgm__ticker");
      this._tickerTrack = this.querySelector(".pgm__tickerTrack");
      this._tickerGroup = this.querySelector(".pgm__tickerGroup"); // primo gruppo
      this._marqueeOn = false;
      this._marqueeX = 0;
      this._marqueeHalfW = 0;
      this._marqueePxPerSec = 0;
      this._marqueeDir = -1;

      // parallax smoothing
      this._targetP = 0;
      this._p = 0;
      this._inited = false;

      // più smoothing su mobile (evita tremolio). desktop più reattivo.
      this._smooth = (window.matchMedia && window.matchMedia("(max-width: 749px)").matches) ? 0.10 : 0.14;

      // reset vars
      for (const layer of this._layers) {
        layer.style.setProperty("--pgm-cx", "0px");
        layer.style.setProperty("--pgm-cy", "0px");
      }

      PGMManager.instances.add(this);
      PGMManager.start();

      this.refresh(true);
    }

    disconnectedCallback() {
      PGMManager.instances.delete(this);
    }

    refresh(force = false) {
      if (prefersReduced) return;
      if (!this._scrollWrap) return;

      // metriche stabili: range basato sull’altezza reale dello STAGE
      // (evita jitter quando cambia viewport/URL bar)
      const y =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      const rect = this._scrollWrap.getBoundingClientRect();
      this._top = rect.top + y;

      const stageH = this._stage ? this._stage.getBoundingClientRect().height : window.innerHeight;
      this._range = Math.max(1, this._scrollWrap.offsetHeight - stageH);

      // Marquee JS sempre (così non “stacca” mentre scrolli)
      if (this._tickerTrack && this._tickerGroup) this._setupMarquee(force);
    }

    _setupMarquee(force = false) {
      if (prefersReduced) return;
      if (this._marqueeOn && !force) return;

      // metà larghezza = larghezza del primo gruppo
      const gw = this._tickerGroup.getBoundingClientRect().width;
      this._marqueeHalfW = Math.max(1, gw);

      // speed in secondi da CSS var --pgm-ticker-speed (es "26s")
      const style = getComputedStyle(this);
      const speedStr = style.getPropertyValue("--pgm-ticker-speed").trim().replace("s", "");
      const speedSec = Math.max(6, parseFloat(speedStr) || 26);

      // px/s = halfWidth / seconds
      this._marqueePxPerSec = this._marqueeHalfW / speedSec;

      // direction
      this._marqueeDir = (this._ticker && this._ticker.dataset.direction === "right") ? 1 : -1;

      this._marqueeOn = true;
      this.classList.add("pgm--js-marquee");

      // non resettare a 0 (evita jump); normalizza solo
      this._marqueeX = ((this._marqueeX % this._marqueeHalfW) + this._marqueeHalfW) % this._marqueeHalfW;
      this._tickerTrack.style.setProperty("--pgm-marquee-x", "0px");
    }

    _updateParallax(scrollY) {
      const rawP = clamp((scrollY - this._top) / this._range, 0, 1);
      this._targetP = rawP;

      if (!this._inited) {
        this._p = rawP;
        this._inited = true;
      } else {
        this._p = lerp(this._p, this._targetP, this._smooth);
      }

      const p = this._p;

      for (const layer of this._layers) {
        const start = parseFloat(layer.dataset.start || "0");
        const end = parseFloat(layer.dataset.end || "1");

        const sx = parseFloat(layer.dataset.sx || "0");
        const ex = parseFloat(layer.dataset.ex || "0");
        const sy = parseFloat(layer.dataset.sy || "0");
        const ey = parseFloat(layer.dataset.ey || "0");

        const ss = parseFloat(layer.dataset.ss || "1");
        const es = parseFloat(layer.dataset.es || "1");

        const fade = layer.dataset.fade === "true";

        const denom = Math.max(0.0001, end - start);
        const tRaw = clamp((p - start) / denom, 0, 1);
        const t = easeInOut(tRaw);

        // quantizza a 0.5px = meno tremolio
        const tx = q05(lerp(sx, ex, t));
        const ty = q05(lerp(sy, ey, t));
        const sc = lerp(ss, es, t);

        layer.style.setProperty("--pgm-tx", `${tx}px`);
        layer.style.setProperty("--pgm-ty", `${ty}px`);
        layer.style.setProperty("--pgm-scale", `${sc.toFixed(3)}`);
        layer.style.setProperty("--pgm-op", fade ? `${tRaw.toFixed(3)}` : "1");
      }
    }

    _updateMarquee(now, dt) {
      if (!this._marqueeOn || !this._tickerTrack) return;

      // px step
      const step = this._marqueePxPerSec * (dt / 1000);
      this._marqueeX += step;

      // wrap
      if (this._marqueeX >= this._marqueeHalfW) this._marqueeX -= this._marqueeHalfW;

      const x = this._marqueeDir * this._marqueeX;

      // usa anche qui 0.5px
      const xx = q05(-x);
      this._tickerTrack.style.setProperty("--pgm-marquee-x", `${xx}px`);
    }

    _tick(now, scrollY) {
      if (prefersReduced) return;
      if (!this._scrollWrap) return;

      if (!this._lastNow) this._lastNow = now;
      const dt = Math.min(40, now - this._lastNow);
      this._lastNow = now;

      this._updateParallax(scrollY);
      this._updateMarquee(now, dt);
    }
  }

  if (!customElements.get("parallax-gallery-marquee")) {
    customElements.define("parallax-gallery-marquee", ParallaxGalleryMarquee);
  }

  document.addEventListener("shopify:section:load", (e) => {
    const el = e.target.querySelector("parallax-gallery-marquee");
    if (el && el.refresh) el.refresh(true);
  });
})();
